import React from 'react';
import { Star, Quote, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTestimonials } from '../hooks/useData';
import LoadingSpinner from './ui/LoadingSpinner';
import Carousel from './ui/Carousel';

interface TestimonialCardProps {
  testimonial: {
    id: number;
    name: string;
    profession: string;
    quote: string;
    rating: number;
  };
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ testimonial }) => (
  <div className="card card-pad relative mt-6">
    {/* Quote icon */}
    <div className="absolute -top-2 -left-2 w-10 h-10 bg-brand-accent rounded-full flex items-center justify-center z-10">
      <Quote className="w-5 h-5 text-brand-sand" />
    </div>
    
    <div className="pt-6">
      <blockquote className="text-lg text-brand-ink leading-relaxed mb-6 italic">
        "{testimonial.quote}"
      </blockquote>
      
      <div className="border-t border-brand-slate/10 pt-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-brand-slate">{testimonial.name}</p>
            <p className="text-sm text-brand-ink/70">{testimonial.profession}</p>
          </div>
          
          <div className="flex items-center gap-1">
            {[...Array(testimonial.rating)].map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-brand-accent text-brand-accent" />
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Testimonials: React.FC = () => {
  const { t } = useTranslation();
  const { testimonialsData, loading, error } = useTestimonials();

  // Fallback estático de depoimentos para quando a API falhar
  const fallbackTestimonials = [
    {
      id: 1,
      name: "João Silva",
      profession: "Trabalhador Celetista",
      quote: "A equipe foi essencial para garantir que meus direitos não fossem deixados de lado. Recomendo o trabalho ético e ágil.",
      rating: 5
    },
    {
      id: 2,
      name: "Sérgio Almeida",
      profession: "Síndico Profissional",
      quote: "Com a assessoria, nosso condomínio reduziu a inadimplência em tempo recorde. A comunicação é clara e objetiva.",
      rating: 5
    },
    {
      id: 3,
      name: "Marcia Fernandes",
      profession: "Empresária",
      quote: "O trabalho preventivo evitou passivos trabalhistas enormes na minha empresa. Sinto muito mais segurança nas decisões de RH.",
      rating: 5
    }
  ];

  const dataToUse = error || (!loading && testimonialsData.length === 0) ? fallbackTestimonials : testimonialsData;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section className="section bg-white">
      <div className="wrapper">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
          className="max-w-6xl mx-auto"
        >
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-heading text-brand-slate mb-16 text-center">
            O que dizem sobre o atendimento
          </motion.h2>

          {loading ? (
            <LoadingSpinner message="Carregando depoimentos..." />
          ) : (
            <div className="mb-12">
              <Carousel
                autoPlay={true}
                autoPlayInterval={5000}
                showControls={true}
                showDots={true}
                itemsPerView={2}
                className="max-w-6xl mx-auto"
              >
                {dataToUse.map((testimonial) => (
                  <TestimonialCard key={testimonial.id} testimonial={testimonial} />
                ))}
              </Carousel>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center"
          >
            <p className="text-sm text-brand-ink/60 italic">
              {t('testimonials.note')}
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-20 border-t border-brand-slate/10 pt-16"
          >
            <h3 className="text-2xl md:text-3xl font-heading text-brand-slate mb-8 text-center">
              {t('testimonials.cases.title')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-brand-sand rounded-xl p-8 border border-brand-slate/10">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="text-brand-accent w-6 h-6" />
                  <h4 className="text-xl font-semibold text-brand-slate">{t('testimonials.cases.condominium.title')}</h4>
                </div>
                <p className="text-brand-ink/80 leading-relaxed text-sm">
                  {t('testimonials.cases.condominium.text')}
                </p>
              </div>

              <div className="bg-brand-sand rounded-xl p-8 border border-brand-slate/10">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="text-brand-accent w-6 h-6" />
                  <h4 className="text-xl font-semibold text-brand-slate">{t('testimonials.cases.worker.title')}</h4>
                </div>
                <p className="text-brand-ink/80 leading-relaxed text-sm">
                  {t('testimonials.cases.worker.text')}
                </p>
              </div>
            </div>
            <p className="text-center text-xs text-brand-ink/60 italic mt-6">
              {t('testimonials.cases.disclaimer')}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;